import { useMemo } from "react";
import DataTable from "./DataTable";
import CollapsibleSection from "./CollapsibleSection";
import { fmtAed } from "./KpiCard";
import type { FrameRecords, SideDisplayConfig, OtherSections, PriorOthers } from "../types/api";

interface Props {
  frame: FrameRecords;
  config?: SideDisplayConfig;
  // The CRI frame + its display config. Out-of-period rows are usually CRI-side (a CRI
  // export routinely carries the following month), so both sides are classified.
  criFrame?: FrameRecords;
  criConfig?: SideDisplayConfig;
  // Section taxonomy from the API (titles / hints / Difference flags), shared with the
  // Excel Others sheet. Absent on an older payload — the tab then falls back to keys.
  sections?: OtherSections;
  // The window's From bound (ISO), for the before/after split. Undefined = no window.
  periodFrom?: string | null;
  insurerName?: string;  // scopes pinned columns on the insurer-side table
  // The previous output's Others rows carried into this run. Not engine rows, so they
  // come pre-shaped with the engine's own total rather than being classified here.
  priorOthers?: PriorOthers | null;
  // The prior-period list's still-open claims (rows flagged unrec_claim) and the engine's
  // total for them, listed here instead of on Group 1.
  priorClaims?: FrameRecords | null;
  priorClaimsTotal?: number;
}

// Section keys — must match reporting/other_sections.py.
const BEFORE_PERIOD = "before_period";
const UNDATED = "undated";
const UNIDENTIFIED = "unidentified";
const HELD_OUT = "held_out";
const SETTLED = "settled";
const CLAIMS = "claims";
const PRIOR_OTHERS = "prior_others";
const PRIOR_CLAIMS = "prior_claims";
// UNDATED is deliberately absent: the "Undated transactions" reference block below
// lists every undated row once, and this section was always a subset of it (server
// side: OTHER_SECTION_KEYS drops it too).
const FALLBACK_ORDER = [BEFORE_PERIOD, UNIDENTIFIED, HELD_OUT, SETTLED, CLAIMS, PRIOR_CLAIMS, PRIOR_OTHERS];

// Sections that are informational (already in a balance, or their own track) start
// collapsed; the two that actually reach the Difference start open, because those are
// the rows finance is here to action.
const DEFAULT_OPEN = new Set([UNIDENTIFIED, HELD_OUT]);

// First 10 chars of an ISO date cell, or "" when the cell isn't a date.
function isoDay(v: unknown): string {
  const s = String(v ?? "").slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : "";
}

// Other tab — the residual population, split into the sections defined server-side in
// reporting/other_sections.py and rendered with the SAME titles/hints/Difference flags
// the Excel Others sheet uses.
//
// Rows dated AFTER the window are deliberately NOT listed: they are in no balance, no
// bucket, no count and no period this run covers, so listing them beside genuine
// residuals invited them to be chased as problems. They stay fully visible on the CRI /
// Insurer Statement tabs (unfiltered dumps of the parsed frames); the count note below
// states the omission rather than leaving it silent.
//
// The before/after distinction is load-bearing, not cosmetic: the windowed closing is
// opening-as-of(From) + movement in [From, To], so a PRE-window row is already inside
// the reported balance while a POST-window row is in no figure at all.
export default function OtherTab({ frame, config, criFrame, criConfig, sections, periodFrom, insurerName = "", priorOthers, priorClaims, priorClaimsTotal = 0 }: Props) {
  const order = sections?.order ?? FALLBACK_ORDER;
  const title = (k: string) => sections?.titles?.[k] ?? k;
  const hint = (k: string) => sections?.hints?.[k] ?? "";
  const inDiff = (k: string) => Boolean(sections?.in_difference?.[k]);
  const badge = (k: string) =>
    inDiff(k) ? (sections?.badge_in ?? "IN THE DIFFERENCE")
              : (sections?.badge_not_in ?? "NOT in the Difference");

  // One pass per frame producing {sectionKey -> row positions} plus the post-period
  // count and per-section subtotal. Mirrors classify_other_sections: out-of-window
  // rows split by doc_date against periodFrom; in-window "Other" split by whether a
  // schema rule fired (insurer `role`); insurer "Claim" rows listed for reference.
  const classify = (f: FrameRecords | undefined, isCri: boolean) => {
    const empty = { buckets: new Map<string, number[]>(), post: 0, totals: new Map<string, number>() };
    if (!f || !f.rows.length) return empty;
    const cCat = f.columns.indexOf("recon_category");
    const cRole = f.columns.indexOf("role");
    const cSettled = f.columns.indexOf("is_settled_paid");
    const cWin = f.columns.indexOf("in_period_window");
    const cDate = f.columns.indexOf("doc_date");
    const cAmt = isCri
      ? f.columns.indexOf("cri_amount")
      : (f.columns.includes("balance_amount")
          ? f.columns.indexOf("balance_amount")
          : f.columns.indexOf("signed_amount"));

    const buckets = new Map<string, number[]>();
    const totals = new Map<string, number>();
    let post = 0;
    const put = (k: string, i: number, amt: number) => {
      let b = buckets.get(k);
      if (!b) { b = []; buckets.set(k, b); }
      b.push(i);
      totals.set(k, (totals.get(k) ?? 0) + amt);
    };

    for (let i = 0; i < f.rows.length; i++) {
      const r = f.rows[i];
      const cat = cCat >= 0 ? String(r[cCat] ?? "") : "";
      // Absent column (no window applied) => in-period, matching the engine's
      // fillna(True) read.
      const inWin = cWin >= 0 ? r[cWin] !== false : true;
      const amt = cAmt >= 0 && typeof r[cAmt] === "number" ? (r[cAmt] as number) : 0;

      if (!inWin) {
        const d = cDate >= 0 ? isoDay(r[cDate]) : "";
        if (!d) {
          // Undated: listed by the "Undated transactions" reference block, not here.
          // Counted into UNDATED only so the bucket totals stay comparable with the
          // server classifier; `order` omits the key, so no section renders it.
          put(UNDATED, i, amt);
        } else if (periodFrom && d < periodFrom) {
          put(BEFORE_PERIOD, i, amt);      // already inside the reported balance
        } else {
          post++;                          // after the window: not listed by design
        }
        continue;
      }
      if (cat === "Claim") {
        if (!isCri) put(CLAIMS, i, amt);
        continue;
      }
      if (cat === "Other") {
        const role = cRole >= 0 ? String(r[cRole] ?? "") : "";
        if (isCri || cRole < 0 || role === "Other") put(UNIDENTIFIED, i, amt);
        // A settled held-out row carries 0.00 (outstanding_paid_gate); a settled row no
        // rule identified stays Unidentified.
        else put(cSettled >= 0 && r[cSettled] === true ? SETTLED : HELD_OUT, i, amt);
      }
    }
    return { buckets, post, totals };
  };

  const ins = useMemo(() => classify(frame, false), [frame, periodFrom]);
  const cri = useMemo(() => classify(criFrame, true), [criFrame, periodFrom]);

  // Cross-cutting UNDATED reference view: every row with no usable transaction date,
  // whichever section or bucket already holds it. Mirrors reporting/other_sections.py
  // ::undated_mask — an undated row is never date-tested, so which section it lands in
  // depends on its insurer's recon_basis and whether a window was set. This answers
  // "which transactions are undated?" directly. Overlaps the sections above by design,
  // so it is labelled reference-only and its subtotal joins no net.
  const undated = useMemo(() => {
    const scan = (f: FrameRecords | undefined, isCri: boolean) => {
      if (!f || !f.rows.length) return { pos: [] as number[], total: 0 };
      const cDate = f.columns.indexOf("doc_date");
      const cAmt = isCri
        ? f.columns.indexOf("cri_amount")
        : (f.columns.includes("balance_amount")
            ? f.columns.indexOf("balance_amount")
            : f.columns.indexOf("signed_amount"));
      const pos: number[] = [];
      let total = 0;
      for (let i = 0; i < f.rows.length; i++) {
        // No date column at all => nothing can be dated, so every row is undated
        // (matches the Python mask, which returns all-True for that frame).
        if (cDate >= 0 && isoDay(f.rows[i][cDate])) continue;
        pos.push(i);
        const v = cAmt >= 0 ? f.rows[i][cAmt] : null;
        if (typeof v === "number") total += v;
      }
      return { pos, total };
    };
    const c = scan(criFrame, true);
    const i = scan(frame, false);
    return { cri: c, ins: i, n: c.pos.length + i.pos.length, total: c.total + i.total };
  }, [frame, criFrame]);

  // Slice a frame down to one section's rows. Kept lazy per section so a collapsed
  // section costs nothing beyond its counts.
  const slice = (f: FrameRecords | undefined, positions: number[] | undefined): FrameRecords | null => {
    if (!f || !positions || !positions.length) return null;
    return {
      columns: f.columns,
      rows: positions.map((p) => f.rows[p]),
      indices: positions.map((p) => f.indices[p]),
      total_rows: positions.length,
    };
  };

  const postTotal = ins.post + cri.post;

  return (
    <div>
      {order.map((key) => {
        if (key === PRIOR_CLAIMS) {
          if (!priorClaims?.rows.length) return null;
          return (
            <CollapsibleSection
              key={key}
              title={title(key)}
              badge={badge(key)}
              badgeTone={inDiff(key) ? "in" : "out"}
              hint={hint(key)}
              sub={`${fmtAed(priorClaimsTotal)}   (${priorClaims.rows.length.toLocaleString()} items)`}
              defaultOpen={false}
            >
              <p className="muted" style={{ margin: "0 0 4px" }}>
                Insurer ({priorClaims.rows.length.toLocaleString()})
              </p>
              <DataTable frame={priorClaims} pageSize={50} pinSide="insurer"
                insurerName={insurerName} title="Prior-period claims - Insurer" />
            </CollapsibleSection>
          );
        }
        if (key === PRIOR_OTHERS) {
          if (!priorOthers?.count) return null;
          return (
            <CollapsibleSection
              key={key}
              title={title(key)}
              badge={badge(key)}
              badgeTone={inDiff(key) ? "in" : "out"}
              hint={hint(key)}
              sub={`${fmtAed(priorOthers.total)}   (${priorOthers.count.toLocaleString()} items)`}
              defaultOpen={false}
            >
              {priorOthers.cri && priorOthers.cri.rows.length > 0 && (
                <>
                  <p className="muted" style={{ margin: "0 0 4px" }}>
                    CRI ({priorOthers.cri.rows.length.toLocaleString()})
                  </p>
                  <DataTable frame={priorOthers.cri} pageSize={50} pinSide="cri"
                    insurerName={insurerName} title="Previous Others - CRI" />
                </>
              )}
              {priorOthers.insurer && priorOthers.insurer.rows.length > 0 && (
                <>
                  <p className="muted" style={{ margin: priorOthers.cri?.rows.length ? "10px 0 4px" : "0 0 4px" }}>
                    Insurer ({priorOthers.insurer.rows.length.toLocaleString()})
                  </p>
                  <DataTable frame={priorOthers.insurer} pageSize={50} pinSide="insurer"
                    insurerName={insurerName} title="Previous Others - Insurer" />
                </>
              )}
            </CollapsibleSection>
          );
        }
        const iPos = ins.buckets.get(key);
        const cPos = cri.buckets.get(key);
        const n = (iPos?.length ?? 0) + (cPos?.length ?? 0);
        if (!n) return null;
        const subtotal = (ins.totals.get(key) ?? 0) + (cri.totals.get(key) ?? 0);
        const criSlice = slice(criFrame, cPos);
        const insSlice = slice(frame, iPos);
        return (
          <CollapsibleSection
            key={key}
            title={title(key)}
            badge={badge(key)}
            badgeTone={inDiff(key) ? "in" : "out"}
            hint={hint(key)}
            sub={`${fmtAed(subtotal)}   (${n.toLocaleString()} items)`}
            defaultOpen={DEFAULT_OPEN.has(key)}
          >
            {criSlice && (
              <>
                <p className="muted" style={{ margin: "0 0 4px" }}>
                  CRI ({criSlice.total_rows.toLocaleString()})
                </p>
                <DataTable frame={criSlice} pageSize={50} config={criConfig}
                  pinSide="cri" insurerName={insurerName} title="CRI" keyColumnsFirst />
              </>
            )}
            {insSlice && (
              <>
                <p className="muted" style={{ margin: criSlice ? "10px 0 4px" : "0 0 4px" }}>
                  Insurer ({insSlice.total_rows.toLocaleString()})
                </p>
                <DataTable frame={insSlice} pageSize={50} config={config}
                  pinSide="insurer" insurerName={insurerName} title="Insurer" keyColumnsFirst />
              </>
            )}
          </CollapsibleSection>
        );
      })}
      {undated.n > 0 && (
        <CollapsibleSection
          key={sections?.undated_all_key ?? "undated_all"}
          title={sections?.undated_all_title ?? "Undated transactions"}
          badge="REFERENCE"
          badgeTone="out"
          hint={sections?.undated_all_hint
            ?? "Every row with no usable transaction date, from both sides, gathered here for reference."}
          sub={`${fmtAed(undated.total)}   (${undated.n.toLocaleString()} items)`}
          defaultOpen={false}
        >
          {undated.cri.pos.length > 0 && criFrame && (
            <>
              <p className="muted" style={{ margin: "0 0 4px" }}>
                CRI ({undated.cri.pos.length.toLocaleString()})
              </p>
              <DataTable frame={slice(criFrame, undated.cri.pos)!} pageSize={50} config={criConfig}
                pinSide="cri" insurerName={insurerName} title="Undated - CRI" keyColumnsFirst />
            </>
          )}
          {undated.ins.pos.length > 0 && (
            <>
              <p className="muted" style={{ margin: undated.cri.pos.length ? "10px 0 4px" : "0 0 4px" }}>
                Insurer ({undated.ins.pos.length.toLocaleString()})
              </p>
              <DataTable frame={slice(frame, undated.ins.pos)!} pageSize={50} config={config}
                pinSide="insurer" insurerName={insurerName} title="Undated - Insurer" keyColumnsFirst />
            </>
          )}
        </CollapsibleSection>
      )}
      {postTotal > 0 && (
        <p className="muted" style={{ marginTop: 12, fontSize: "0.82rem" }}>
          {(sections?.post_period_note ?? "{n} row(s) dated after the To date are not listed here.")
            .replace("{n}", postTotal.toLocaleString())}
        </p>
      )}
      {!order.some((k) => (ins.buckets.get(k)?.length ?? 0) + (cri.buckets.get(k)?.length ?? 0) > 0)
        && postTotal === 0 && undated.n === 0 && !priorOthers?.count && !priorClaims?.rows.length
        && <p className="muted">Nothing in Other.</p>}
    </div>
  );
}
